<template>
  <v-dialog v-model="dialog" max-width="700" @keydown.esc="cancel()" persistent :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title class="white--text">{{
          newDef ? $t('work_methods.add_attribute') : $t('work_methods.edit_attribute')
        }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancel">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-5">
        <v-alert v-if="!newDef" outlined dense type="warning">{{ $t('work_methods.attribute_key_warning') }}</v-alert>
        <v-form ref="form">
          <v-text-field
            :disabled="saving"
            outlined
            dense
            :label="$t('work_methods.label')"
            :rules="[$rules.required]"
            v-model="def.label"
            validate-on-blur
          />
          <v-text-field
            :disabled="saving"
            outlined
            dense
            :label="$t('work_methods.attribute_key')"
            :rules="[$rules.required]"
            v-model="def.attributeKey"
            validate-on-blur
          />
          <v-select
            :disabled="saving"
            outlined
            dense
            :label="$t('work_methods.value_type')"
            :rules="[$rules.required]"
            :items="valueTypeOptions"
            v-model="def.valueType"
          />
          <v-select
            :disabled="saving"
            outlined
            dense
            :label="$t('work_methods.scope')"
            :rules="[$rules.required]"
            :items="scopeOptions"
            v-model="def.scope"
          />
          <v-autocomplete
            v-if="def.valueType === 'ITEMREF'"
            :disabled="saving"
            outlined
            dense
            multiple
            small-chips
            deletable-chips
            :label="$t('work_methods.item_types')"
            :rules="[itemTypesRequired]"
            :items="itemTypeOptions"
            v-model="def.itemTypeIds"
          />
          <v-text-field :disabled="saving" outlined dense :label="$t('work_methods.unit')" v-model="def.unit" />
          <v-text-field :disabled="saving" outlined dense :label="$t('work_methods.icon')" v-model="def.icon" />
          <v-switch :disabled="saving" inset :label="$t('work_methods.show_in_report')" v-model="def.showInReport" />
          <v-switch
            v-if="showCumulative"
            :disabled="saving"
            inset
            :label="$t('work_methods.cumulative')"
            :hint="$t('work_methods.cumulative_hint')"
            persistent-hint
            v-model="def.cumulative"
          />

          <template v-if="def.valueType === 'SELECT'">
            <div class="subtitle-2 mb-2">{{ $t('work_methods.options') }}</div>
            <v-row v-for="(option, index) in def.options" :key="index" dense>
              <v-col cols="5">
                <v-text-field
                  :disabled="saving"
                  outlined
                  dense
                  :label="$t('work_methods.option_value')"
                  :rules="[$rules.required]"
                  v-model="option.value"
                />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  :disabled="saving"
                  outlined
                  dense
                  :label="$t('work_methods.option_label')"
                  v-model="option.label"
                />
              </v-col>
              <v-col cols="2" class="d-flex align-center">
                <v-btn icon @click="removeOption(index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-btn text color="primary" @click="addOption">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('work_methods.add_option') }}
            </v-btn>
          </template>
        </v-form>
        <v-alert :value="showAlert" outlined type="error">{{ $t('validation.check_all_required_fields') }}</v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="default" @click.native="cancel()">{{ $t('cancel') }}</v-btn>
        <v-btn color="primary" :loading="saving" @click.native="save()">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import workMethodsApi from '@/api/workMethods';
import { allItemTypes, loadItemTypes } from '@/helpers/itemTypes';

const defTemplate = {
  id: null,
  attributeKey: '',
  label: '',
  valueType: null,
  scope: null,
  unit: '',
  showInReport: true,
  itemTypeIds: [],
  icon: '',
  cumulative: false,
  options: [],
};

export default {
  name: 'AttributeDefDialog',
  data() {
    return {
      dialog: false,
      saving: false,
      showAlert: false,
      newDef: true,
      methodId: null,
      def: { ...defTemplate },
      valueTypeOptions: ['NUMBER', 'TEXT', 'SELECT', 'BOOLEAN', 'DATE', 'ITEMREF'].map(type => ({
        text: this.$t('work_methods.value_types.' + type),
        value: type,
      })),
      scopeOptions: ['WORK', 'DAY'].map(scope => ({
        text: this.$t('work_methods.scopes.' + scope),
        value: scope,
      })),
    };
  },
  computed: {
    // Käytössä olevat tyypit sekä jo valitut (käytöstä poistettu tyyppi säilyy valittuna)
    itemTypeOptions() {
      return allItemTypes()
        .filter(type => type.active || this.def.itemTypeIds.includes(type.id))
        .map(type => ({ text: type.pluralName, value: type.id }));
    },
    // Kertymä lasketaan backendissä vain NUMBER-tyyppisille päiväarvoille
    showCumulative() {
      return this.def.valueType === 'NUMBER' && this.def.scope === 'DAY';
    },
  },
  methods: {
    open(methodId, existingDef) {
      loadItemTypes().catch(error => this.$handleApiError(error));
      this.dialog = true;
      this.showAlert = false;
      this.methodId = methodId;
      this.newDef = existingDef == null;
      this.$refs.form?.resetValidation();
      if (existingDef == null) {
        this.def = { ...defTemplate, itemTypeIds: [], options: [] };
      } else {
        this.def = {
          id: existingDef.id,
          attributeKey: existingDef.attributeKey,
          label: existingDef.label,
          valueType: existingDef.valueType,
          scope: existingDef.scope,
          unit: existingDef.unit,
          showInReport: existingDef.showInReport === true,
          itemTypeIds: [...(existingDef.itemTypeIds || [])],
          icon: existingDef.icon,
          cumulative: existingDef.cumulative === true,
          options: (existingDef.options || []).map(o => ({ value: o.value, label: o.label })),
        };
      }
    },
    cancel() {
      this.dialog = false;
      this.def = { ...defTemplate, itemTypeIds: [], options: [] };
      this.showAlert = false;
    },
    itemTypesRequired(value) {
      return (value && value.length > 0) || this.$t('validation.field_is_required');
    },
    addOption() {
      this.def.options.push({ value: '', label: '' });
    },
    removeOption(index) {
      this.def.options.splice(index, 1);
    },
    async save() {
      const selectWithoutOptions =
        this.def.valueType === 'SELECT' && (this.def.options.length === 0 || this.def.options.some(o => !o.value));
      const itemRefWithoutTypes = this.def.valueType === 'ITEMREF' && this.def.itemTypeIds.length === 0;
      if (!this.def.label || !this.def.attributeKey || !this.def.valueType || !this.def.scope
        || selectWithoutOptions || itemRefWithoutTypes) {
        this.showAlert = true;
        return;
      }
      const payload = {
        attributeKey: this.def.attributeKey,
        label: this.def.label,
        valueType: this.def.valueType,
        scope: this.def.scope,
        unit: this.def.unit || null,
        showInReport: this.def.showInReport,
        itemTypeIds: this.def.valueType === 'ITEMREF' ? this.def.itemTypeIds : [],
        icon: this.def.icon || null,
        cumulative: this.showCumulative && this.def.cumulative,
        options:
          this.def.valueType === 'SELECT'
            ? this.def.options.map((o, index) => ({ value: o.value, label: o.label || o.value, sortOrder: index + 1 }))
            : [],
      };
      try {
        this.saving = true;
        if (this.newDef) {
          await workMethodsApi.createAttributeDef(this.methodId, payload);
          this.$emit('created');
        } else {
          await workMethodsApi.updateAttributeDef(this.def.id, payload);
          this.$emit('updated');
        }
        this.cancel();
      } catch (err) {
        this.$handleApiError(err);
      }
      this.saving = false;
    },
  },
};
</script>
